import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { forwardRef, useMemo, useRef, useState, useContext, useEffect } from 'react';
import { CurrencyInput } from '@paypalcorp/pp-react-currency-input';
import { IntlContext, getCurrencyMaskOptions } from '@paypalcorp/pp-react-intl';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { EditIcon } from '@paypalcorp/pp-react-icons';
import { devWarning, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { css, keyframes, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysSpaceXs = tokens.sysSpaceXs,
    sysSpaceSm = tokens.sysSpaceSm,
    sysSizeIndicatorMd = tokens.sysSizeIndicatorMd,
    sysSizeFieldLg = tokens.sysSizeFieldLg,
    sysTypographyFieldLabelSmLineHeight = tokens.sysTypographyFieldLabelSmLineHeight;
  var scaleNormal = 44 / 64;
  var cursorPosition = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    position: absolute;\n    content: '';\n    top: ", "; // (68-60)/2\n    bottom: ", "; // (68-60)/2\n    border-right: ", " solid;\n    border-right-color: transparent;\n  "])), sysSpaceXs, sysSpaceXs, sysBorderThicknessEmphasis);
  var blinker = keyframes(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    50% {\n      border-right-color: transparent;\n    }\n  "])));
  var animateCursor = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    border-right-color: ", ";\n    animation: ", " 1s step-start infinite;\n  "])), sysColorBackgroundHighContrast, blinker);
  var base = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: base;\n    border-bottom: ", " solid transparent;\n  "])), sysBorderThicknessNormal);
  var centerAlign = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: center_aligned;\n    text-align: center;\n  "])));
  var baseNoFocus = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    &:hover [data-component='hero_currency--view'] {\n      scale: ", ";\n    }\n  "])), 48 / 64);
  var errorTextSpacing = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: error_text_spacing;\n    margin-bottom: ", ";\n  "])), math("".concat(sysTypographyFieldLabelSmLineHeight, " + ").concat(sysSizeIndicatorMd)));
  var view = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: view;\n    display: inline-flex; // scale doesn't work properly in rtl without inline-flex/inline-block. flex is needed for setting order.\n    font-size: ", ";\n    line-height: ", "; // 4px more than font-size\n    transform-origin: left;\n    scale: ", ";\n    pointer-events: none;\n    transition: scale 200ms ease-in-out;\n    > span {\n      display: inline-block;\n    }\n    [dir='rtl'] & {\n      transform-origin: right;\n    }\n  "])), sysSizeFieldLg, math("".concat(sysSizeFieldLg, " + ").concat(sysSpaceXs)), scaleNormal);
  var viewCenter = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: center_view;\n    transform-origin: center;\n    [dir='rtl'] & {\n      transform-origin: center;\n    }\n  "])));
  var textInput = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: text_input;\n    height: 0;\n    & > div:first-of-type {\n      opacity: 0;\n    }\n  "])));
  var focusedView = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: focused_view;\n    scale: ", ";\n  "])), 64 / 64);
  var focusEntered = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    &:after {\n      ", ";\n    }\n    [dir='rtl'] & {\n      &:before {\n        ", ";\n      }\n    }\n  "])), animateCursor, animateCursor);
  var focusImplied = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: focus_implied;\n    color: ", ";\n    font-weight: normal;\n  "])), sysColorBackgroundMediumContrast);
  var focusImpliedDefault = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: focus_implied_default;\n    color: ", ";\n  "])), sysColorBackgroundHighContrast);
  var symbol = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: symbol;\n    color: ", ";\n    [dir='rtl'] & {\n      order: 1;\n    }\n  "])), sysColorBackgroundHighContrast);
  var entered = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: entered;\n    color: ", ";\n    font-weight: normal;\n    position: relative;\n    &:after {\n      ", ";\n    }\n    [dir='rtl'] & {\n      order: 3;\n      direction: ltr;\n      &:after {\n        content: none;\n      }\n      &:before {\n        ", ";\n      }\n    }\n  "])), sysColorBackgroundHighContrast, cursorPosition, cursorPosition);
  var impliedDefault = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: implied_default;\n    color: ", ";\n    [dir='rtl'] & {\n      order: 2;\n    }\n  "])), sysColorBackgroundHighContrast);
  var editIcon = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: edit_icon;\n    margin-inline-start: ", ";\n    align-self: center;\n    pointer-events: auto;\n    scale: ", ";\n    transform-origin: left;\n    &:focus {\n      &:after {\n        box-shadow: none;\n        border: none;\n      }\n    }\n    [dir='rtl'] & {\n      transform-origin: right;\n      order: 4;\n    }\n  "])), sysSpaceSm, 1 / scaleNormal);
  return {
    base: base,
    centerAlign: centerAlign,
    baseNoFocus: baseNoFocus,
    errorTextSpacing: errorTextSpacing,
    textInput: textInput,
    view: view,
    viewCenter: viewCenter,
    focusedView: focusedView,
    focusEntered: focusEntered,
    focusImplied: focusImplied,
    focusImpliedDefault: focusImpliedDefault,
    symbol: symbol,
    entered: entered,
    impliedDefault: impliedDefault,
    editIcon: editIcon
  };
}
var _excluded = ["name", "id", "maskOptions", "currencyType", "prefix", "onAccept", "onFocus", "onBlur", "onKeyDown", "value", "className", "inputClassName", "label", "showEditIcon", "align"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var HeroCurrency = forwardRef(function (_ref, ref) {
  var _ref$name = _ref.name,
    name = _ref$name === void 0 ? 'heroCurrency' : _ref$name,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? name : _ref$id,
    maskOptions = _ref.maskOptions,
    currencyType = _ref.currencyType,
    prefix = _ref.prefix,
    onAccept = _ref.onAccept,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? null : _ref$onFocus,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === void 0 ? null : _ref$onBlur,
    _ref$onKeyDown = _ref.onKeyDown,
    onKeyDown = _ref$onKeyDown === void 0 ? null : _ref$onKeyDown,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    className = _ref.className,
    inputClassName = _ref.inputClassName,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Enter amount' : _ref$label,
    showEditIcon = _ref.showEditIcon,
    _ref$align = _ref.align,
    align = _ref$align === void 0 ? 'left' : _ref$align,
    rest = _objectWithoutProperties(_ref, _excluded);

  /* istanbul ignore next */
  if (!onAccept) {
    onAccept = function () {
      return devWarning('Required prop `onAccept` is missing');
    };
  } // If theme is not directly provided, get from themeContext

  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var internalRef = useRef();
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    entered = _useState2[0],
    setEntered = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    implied = _useState4[0],
    setImplied = _useState4[1];
  var computedRef = ref || internalRef;
  var intlContext = useContext(IntlContext);
  var currencyParts = useMemo(function () {
    return getCurrencyMaskOptions(intlContext, maskOptions, currencyType);
  }, [intlContext, maskOptions, currencyType]);
  var currencyMaskOptions = currencyParts.currencyMaskOptions,
    intlCurrencyPrefix = currencyParts.intlCurrencyPrefix,
    intlCurrencySuffix = currencyParts.intlCurrencySuffix;
  function handleChange() {
    setTimeout(function () {
      if (computedRef === null || computedRef === void 0 ? void 0 : computedRef.current) {
        var result = parseDisplay(computedRef.current.value, currencyMaskOptions.radix, currencyMaskOptions.scale);
        setEntered(result.entered);
        setImplied(result.implied);
      }
    }, 0);
  }
  useEffect(function () {
    handleChange();
  }, [value]);
  var currencySymbol = prefix || intlCurrencyPrefix || intlCurrencySuffix || '$';
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    inputFocused = _useState6[0],
    setInputFocus = _useState6[1];
  var containerCss = [_styles.base, align === 'center' && _styles.centerAlign, !inputFocused && _styles.baseNoFocus, (rest.errorText || rest.helperText || rest.successText) && _styles.errorTextSpacing];
  var viewCss = [_styles.view, inputFocused && _styles.focusedView, align === 'center' && _styles.viewCenter];
  var enteredCss = [_styles.entered, inputFocused && _styles.focusEntered];
  var impliedCss = [_styles.impliedDefault, inputFocused && _styles.focusImplied, inputFocused && value === '' && _styles.focusImpliedDefault];
  /* istanbul ignore next */

  function parseDisplay(str) {
    var decimal = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '.';
    var scale = arguments.length > 2 ? arguments[2] : undefined;
    if (!str || str === '') {
      return {
        entered: '',
        implied: inputFocused ? '' : '0'
      };
    } else if (str && str[0] === decimal) {
      // If first char is decimal then prepend '0'
      str = '0' + str;
    }
    var parts = str.split(decimal);
    return {
      entered: str,
      implied: parts.length > 1 ? new Array(scale + 1).join('0').substring(parts[1].length, scale) : ''
    };
  }
  var handleBlur = function (event) {
    setInputFocus(false);
    /* istanbul ignore if */

    if (entered !== '') {
      var computedUnmaskedValue = entered.replaceAll(currencyMaskOptions.thousandsSeparator, '').replace(currencyMaskOptions.radix, '.');
      onAccept(entered + implied, computedUnmaskedValue + implied, +(computedUnmaskedValue + implied));
    }
    if (onBlur) {
      onBlur(event);
    }
  };
  var options = _objectSpread(_objectSpread({}, currencyMaskOptions), {}, {
    normalizeZeros: false,
    padFractionalZeros: false
  });
  /* istanbul ignore next */

  var inputMode = (currencyMaskOptions === null || currencyMaskOptions === void 0 ? void 0 : currencyMaskOptions.scale) === 0 ? 'numeric' : 'decimal';
  var inputCss = [_styles.textInput];
  return jsx("div", {
    className: className,
    css: containerCss,
    id: id,
    "data-ppui-info": "hero-currency_2.5.7"
  }, jsx(CurrencyInput, _extends({
    name: name,
    label: label,
    inputMode: inputMode,
    value: value,
    maskOptions: options,
    inputClassName: inputClassName,
    css: inputCss,
    onFocus: function handleFocus(event) {
      setInputFocus(true);
      var target = event.target;
      var val = target.value;
      /* istanbul ignore next */

      if (val) {
        // move the cursor to end for cases when user clicks on the middle of hidden input
        // setTimeout is necessary unfortunately
        setTimeout(function () {
          return target.setSelectionRange(val.length, val.length);
        }, 100);
      }
      if (onFocus) {
        onFocus(event);
      }
    },
    onBlur: handleBlur,
    onAccept: onAccept,
    onKeyDown: function handleKeys(event) {
      /* istanbul ignore next */
      var KeyCode = new KeyboardCode(event.key || event.keyCode);
      if (KeyCode.IS_SIDE_NAVIGATION) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    onInput: handleChange,
    prefix: currencySymbol,
    autoComplete: "off",
    ref: computedRef
  }, rest, {
    "data-ppui": "true"
  })), jsx("div", {
    "data-component": "hero_currency--view",
    css: viewCss,
    "aria-hidden": "true",
    "data-ppui": "true"
  }, jsx("span", {
    css: [_styles.symbol],
    "data-ppui": "true"
  }, currencySymbol), jsx("span", {
    css: enteredCss,
    "data-ppui": "true"
  }, entered), jsx("span", {
    css: impliedCss,
    "data-ppui": "true"
  }, implied), !inputFocused && showEditIcon && value && jsx(IconButton, {
    buttonType: "tertiary",
    svgIconComponent: EditIcon,
    css: _styles.editIcon,
    onClick: function focusViaButton() {
      /* istanbul ignore next */
      if (computedRef === null || computedRef === void 0 ? void 0 : computedRef.current) {
        computedRef.current.focus();
      }
    },
    tabIndex: "-1",
    "data-ppui": "true"
  })));
});
export { HeroCurrency, styles as heroCurrencyStyles };